import type { ToolbarAction } from '../common/types';

type OverflowAction =
	| { type: 'SET_MUST_RECALCULATE' }
	| { payload: Set<number>; type: 'SET_OVERFLOW_BUTTONS' };

type OverflowState = {
	mustRecalculateOverflowButtons: boolean;
	overflowButtons: Set<number>;
};

export const overflowReducer = (state: OverflowState, action: OverflowAction): OverflowState => {
	switch (action.type) {
		case 'SET_MUST_RECALCULATE':
			return { ...state, mustRecalculateOverflowButtons: true, overflowButtons: new Set() };
		case 'SET_OVERFLOW_BUTTONS':
			return { ...state, mustRecalculateOverflowButtons: false, overflowButtons: action.payload };
		default:
			return state;
	}
};

export const calculateOverflowButtons = (
	containerRef: React.RefObject<HTMLElement>,
	buttonRefs: Map<number, HTMLButtonElement | null>,
	buttonContainerRef: React.RefObject<HTMLElement>,
	takenWidth: number,
	actions: ToolbarAction[],
): Set<number> => {
	if (!containerRef.current) {
		return new Set<number>();
	}

	const overflowButtons: Set<number> = new Set();
	const containerWidth = containerRef.current.offsetWidth;
	const buttonContainerWidth = buttonContainerRef.current?.scrollWidth || 0;

	// If everything fits, no overflow needed
	if (containerWidth > buttonContainerWidth) {
		return overflowButtons;
	}

	const usedWidth = takenWidth;

	// Create array of buttons with their priorities (keep original order)
	const buttonData: Array<{
		action: ToolbarAction;
		button: HTMLButtonElement;
		index: number;
		isAlwaysVisible: boolean;
		priority: number;
		width: number;
	}> = [];

	buttonRefs.forEach((button, index) => {
		if (!button) {
			return;
		}

		const actionIndex = Array.from(buttonRefs.keys()).indexOf(index);
		const action = actions[actionIndex];

		if (!action) {
			return;
		}

		const priority = action.hiddenPriority ?? 1000 + actionIndex;
		const isAlwaysVisible = priority < 0;

		buttonData.push({
			index,
			button,
			action,
			width: button.offsetWidth,
			priority,
			isAlwaysVisible,
		});
	});

	// Calculate total width needed for always-visible buttons
	const alwaysVisibleWidth = buttonData
		.filter((btn) => btn.isAlwaysVisible)
		.reduce((sum, btn) => sum + btn.width, 0);

	const availableWidthForOptional = containerWidth - usedWidth - alwaysVisibleWidth;

	// Sort optional buttons by priority (highest priority = hidden first)
	const optionalButtons = buttonData
		.filter((btn) => !btn.isAlwaysVisible)
		.sort((a, b) => a.priority - b.priority);

	// Determine which optional buttons can fit
	let optionalWidthUsed = 0;
	for (const buttonInfo of optionalButtons) {
		if (optionalWidthUsed + buttonInfo.width <= availableWidthForOptional) {
			optionalWidthUsed += buttonInfo.width;
		} else {
			// This button and all remaining buttons go to overflow
			overflowButtons.add(buttonInfo.index);
		}
	}

	return overflowButtons;
};

export const initialOverflowState: OverflowState = {
	mustRecalculateOverflowButtons: false,
	overflowButtons: new Set(),
};
